import { memo, useCallback, useContext, useMemo } from 'react';
import { ResponsiveLine } from '@nivo/line';
import type { LineChart, MultipleLineChart } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context';
import { ChartHoverText } from './Hover';
import { theme } from './theme';
import { formatAxisLabel } from './utils';
import { TemplateTooltip } from './TemplateTooltip';
import { replaceTemplateValues } from './template-utils';

type Props = {
  chart: LineChart | MultipleLineChart;
  colors: string[];
  columnN?: number;
};

export const NivoLineChart = memo((props: Props) => {
  const data = [];

  const returnedData = props.chart.data;
  for (let i = 0; i < returnedData.length; i++) {
    const item = {
      id: returnedData[i].id,
      data: returnedData[i].data,
    };
    data.push(item);
  }

  const [, setOpen] = useContext(LegendCollapseToggleContext);

  const tooltipDataMap = useMemo(() => {
    const map: Record<string, Record<string, string | number> | undefined> = {};
    for (const line of props.chart.data) {
      map[line.id] = line.tooltipData;
    }
    return map;
  }, [props.chart.data]);

  const formatBottomAxisLabel = useCallback((value: string) => {
    return formatAxisLabel({ value });
  }, []);

  return (
    <div style={{ height: 320, width: '100%', position: 'relative' }}>
      <ResponsiveLine
        data={data}
        margin={{ top: 50, right: 50, bottom: 50, left: 50 }}
        xScale={{ type: 'point' }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false,
        }}
        yFormat=" >-.2f"
        axisTop={null}
        axisRight={null}
        colors={props.colors}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: props.chart.xAxisTitle,
          legendOffset: 36,
          legendPosition: 'middle',
          truncateTickAt: 0,
          format: formatBottomAxisLabel,
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: props.chart.yAxisTitle,
          legendOffset: -40,
          legendPosition: 'middle',
          truncateTickAt: 0,
        }}
        pointSize={8}
        // pointColor={{ from: 'color' }}
        pointBorderWidth={2}
        pointBorderColor={{ from: 'seriesColor' }}
        pointLabel="data.yFormatted"
        pointLabelYOffset={-12}
        enableCrosshair={true}
        useMesh={true}
        tooltip={(e) => {
          const data = e.point.data as { xFormatted: string | number; yFormatted: number };
          // If a tooltip template is provided, use it
          if (props.chart.tooltipTemplate) {
            const matchedTooltipData = tooltipDataMap[e.point.seriesId as string];
            const templateValues = {
              xValue: data.xFormatted,
              yValue: data.yFormatted,
              xAxisTitle: props.chart.xAxisTitle,
              yAxisTitle: props.chart.yAxisTitle,
              seriesId: e.point.seriesId as string | number,
              ...matchedTooltipData,
            };
            const content = replaceTemplateValues(props.chart.tooltipTemplate, templateValues);
            return <TemplateTooltip content={content} />;
          }
          // Otherwise use the default tooltip
          return <ChartHoverText name={data.xFormatted} count={data.yFormatted} columnN={props.columnN} />;
        }}
        onClick={(data) => {
          setOpen(true);
        }}
        animate={false}
        theme={theme}
      />
    </div>
  );
});
